import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { TimelineAnimationState, TimelineCanvas, TimelineChartLine, TimelineData } from '../timeline-model';

import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import * as d3Interpolate from 'd3-interpolate-path';

@Component({
  selector: '[chart-line]',
  standalone: true,
  imports: [CommonModule],
  template: `
    <svg:g *ngFor="let chartLine of chartLinesActual; let i = index">
      <!--
        TAINTED PATH: path to show wrong or missing data
        TODO: this dotted line appears all the time, and is not synchronized
         with the animation of the lines, we need to fix this, but not for now
      <svg:path
        class="chart-line-tainted"
        [ngClass]="{
          'fade-in': fadeIn
        }"
        [attr.d]="taintedPath[i]"
        fill="none"
        stroke-opacity="1"
        stroke-width="1.5"
        stroke-dasharray="3 3"
        [attr.stroke]="chartLine.color"
      ></svg:path>

      -->

      <!--
        LINE PATH:
       -->

      <svg:path #lineChartPath class="chart-line" fill="none" [attr.stroke]="chartLine.color"></svg:path>
    </svg:g>
  `,
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent {
  /**
   * ChartLines Input
   */
  public chartLinesActual: Array<TimelineChartLine>;
  public chartLinesIOActual: Array<TimelineChartLine>;

  @Input()
  set chartData(value: Array<TimelineChartLine>) {
    //  We don't want to transition if the data is the same
    if (JSON.stringify(this.chartLinesIOActual) !== JSON.stringify(value)) {
      this.transitionLines();
      this.chartLinesActual = value;
    }
    this.chartLinesIOActual = value;
  }

  @Input()
  dataRange: Array<number>;

  @Input()
  timeRange: Array<number>;

  @Input()
  canvasHorizontalRange: Array<number>;

  @Input()
  canvasVerticalRange: Array<number>;

  /**
   * Canvas
   */

  @Input()
  set canvas(canvas: TimelineCanvas) {
    if (canvas) {
      //  If canvas change is because there was a resize event, so we redraw without transition
      this.resizeLines();
    }
  }

  @Output()
  animationChange: EventEmitter<TimelineAnimationState> = new EventEmitter();

  @ViewChildren('lineChartPath')
  lineChartPath: QueryList<ElementRef>;

  // Path types
  public taintedPath: Array<string> = [];

  // Previous and next to create a transition
  private previousPath: { [key: string]: string } = {};
  private nextPath: { [key: string]: string } = {};

  public fadeIn: boolean;

  private transitionLines() {
    //  Reset Animation
    this.animationChange.emit(TimelineAnimationState.END);
    this.fadeIn = false;
    setTimeout(() => {
      if (this.lineChartPath) {
        this.taintedPath = [];

        this.lineChartPath.forEach((pathElm: ElementRef, index: number) => {
          this.transitionLine(pathElm, this.chartLinesActual[index], index === this.lineChartPath.length - 1);
        });
      }
    }, 100);
  }

  private transitionLine(elm: ElementRef, chartLine: TimelineChartLine, finished: boolean) {
    // Get Category
    const category = chartLine.category;

    //  If there is no previous data to transition, we should generate data with the lowest value to animate
    if (!this.previousPath[category]) {
      this.previousPath[category] = this.getChartLinePath(
        chartLine.data.map((d) => ({
          ...d,
          value: this.dataRange[0]
        }))
      ).line;
    } else {
      //  Store the next path as previous for the next transition
      this.previousPath[category] = this.nextPath[category];
    }
    //  Store the next path
    this.nextPath[category] = this.getChartLinePath(chartLine.data).line;
    //  Store the path coordinates for tainted line
    this.taintedPath.push(this.getChartLinePath(chartLine.data).tainted);

    // Interpolate the line
    d3.select(elm.nativeElement)
      .transition()
      .duration(1200)
      .ease(d3.easeBackOut.overshoot(0.4))
      .attrTween('d', () => d3Interpolate.interpolatePath(this.previousPath[category], this.nextPath[category]))
      .on('end', () => {
        //  we add again the animated class to fadeIn data point dots and filled line

        if (finished) {
          setTimeout(() => {
            this.animationChange.emit(TimelineAnimationState.START);
            this.fadeIn = true;
          }, 100);
        }
      });
  }

  private getChartLinePath(data: Array<TimelineData>): {
    tainted: string;
    line: string;
  } {
    const x = this.timeToCanvasXScale();
    const y = this.valueToCanvasYScale();

    const dataDateObj = data.map((d) => {
      const date = new Date(`${d.date}T00:00:00.000`);
      return {
        ...d,
        date
      };
    });

    const line = d3
      .line()
      .defined((d) => !d.tainted)
      .x((d) => x(d.date))
      .y((d) => y(d.value));

    const lineTained = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.value));

    const linePath = line(dataDateObj);
    // Tainted path is a path that draws all the data but is dotted
    const taintedPath = lineTained(dataDateObj);

    return {
      tainted: taintedPath,
      line: linePath
    };
  }

  private timeToCanvasXScale() {
    return d3.scaleTime().domain(this.timeRange).range(this.canvasHorizontalRange);
  }

  private valueToCanvasYScale() {
    return d3.scaleLinear().domain(this.dataRange).nice().range(this.canvasVerticalRange);
  }

  private resizeLines() {
    setTimeout(() => {
      if (this.lineChartPath) {
        this.taintedPath = [];

        this.lineChartPath.forEach((pathElm: ElementRef, index: number) => {
          // Interpolate the line
          d3.select(pathElm.nativeElement).attr('d', () => this.getChartLinePath(this.chartLinesActual[index].data).line);

          //  Store the path coordinates for filled line
          this.taintedPath.push(this.getChartLinePath(this.chartLinesActual[index].data).tainted);
        });
      }
    }, 0);
  }
}
